import { useState, type FormEvent } from 'react';
import { authApi, ROLE_LABELS } from '../api';
import { useAuth } from '../auth';
import { Alert, Button, Field, PageHeader } from '../components/ui';

export default function AccountPage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState<string>();
  const [nameMessage, setNameMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    setNameMessage(null);
    try {
      setUser(await authApi.updateMe((name ?? user.name).trim()));
      setName(undefined);
      setNameMessage({ kind: 'success', text: 'Name saved.' });
    } catch (err) {
      setNameMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to save name' });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== repeat) {
      setMessage({ kind: 'error', text: 'New passwords do not match.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await authApi.changePassword(current, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      setMessage({ kind: 'success', text: 'Password changed. Other sessions were signed out.' });
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md">
      <PageHeader title="Account" />
      <form onSubmit={saveName} className="mb-6 space-y-3 rounded-lg border border-slate-200 bg-white p-5">
        {nameMessage && <Alert kind={nameMessage.kind}>{nameMessage.text}</Alert>}
        <Field label="Name" required maxLength={100} value={name ?? user.name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" disabled={name === undefined || !name.trim()}>
          Save name
        </Button>
      </form>

      <dl className="mb-8 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-slate-500">Username</dt>
        <dd>{user.username}</dd>
        <dt className="text-slate-500">Role</dt>
        <dd>{ROLE_LABELS[user.role]}</dd>
        <dt className="text-slate-500">Projects</dt>
        <dd>{user.role === 'admin' ? 'All' : user.projects.map((p) => p.name).join(', ') || 'None assigned'}</dd>
      </dl>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="font-medium">Change password</h2>
        {message && <Alert kind={message.kind}>{message.text}</Alert>}
        <Field label="Current password" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        <Field label="New password" type="password" autoComplete="new-password" minLength={10} required value={next} onChange={(e) => setNext(e.target.value)} />
        <Field label="Repeat new password" type="password" autoComplete="new-password" minLength={10} required value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </div>
  );
}
